import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ArrowLeft, Upload, User, Phone, Pill, AlertCircle, FileText, Plus, X, Lock, Image } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import { useSubscription } from "@/hooks/useSubscription";
import { UpgradeModal } from "@/components/UpgradeModal";
import { trackProfileCreation, trackFormSubmission } from "@/utils/analytics";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";

interface EmergencyContact {
  name: string;
  phone: string;
  relation: string;
}

interface Medication {
  name: string;
  notes?: string;
}

interface Allergy {
  name: string;
  notes?: string;
}

interface Condition {
  name: string;
  notes?: string;
  attackStatus?: 'akut' | 'remission' | 'stabil' | '';
}

interface EmergencyProfile {
  id: string;
  photo: string;
  profileImage: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  bloodType: string;
  emergencyContacts: EmergencyContact[];
  medications: Medication[];
  allergies: Allergy[];
  conditions: Condition[];
  additionalNotes: string;
  passwordProtected: boolean;
  password: string;
}

const profilePasswordSchema = z.string()
  .min(10, "Passwort muss mindestens 10 Zeichen lang sein")
  .regex(/[A-Z]/, "Passwort muss mindestens einen Großbuchstaben enthalten")
  .regex(/[a-z]/, "Passwort muss mindestens einen Kleinbuchstaben enthalten")
  .regex(/[0-9]/, "Passwort muss mindestens eine Ziffer enthalten")
  .regex(/[^A-Za-z0-9]/, "Passwort muss mindestens ein Sonderzeichen enthalten");

const profileSchema = z.object({
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  dateOfBirth: z.string().optional(),
  bloodType: z.string().optional(),
  medications: z.array(z.object({
    name: z.string().max(500),
    notes: z.string().max(1000).optional()
  })).max(50),
  allergies: z.array(z.object({
    name: z.string().max(500),
    notes: z.string().max(1000).optional()
  })).max(50),
  conditions: z.array(z.object({
    name: z.string().max(500),
    notes: z.string().max(1000).optional(),
    attackStatus: z.enum(['akut', 'remission', 'stabil', '']).optional()
  })).max(50),
  additionalNotes: z.string().max(2000).optional(),
  emergencyContacts: z.array(
    z.object({
      name: z.string().max(100),
      phone: z.string().max(20),
      relation: z.string().max(50),
    })
  ).max(10),
});

const setProfilePassword = async (profileId: string, password: string): Promise<void> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error("Not authenticated");
  }

  const { error } = await supabase.functions.invoke('set-profile-password', {
    body: { profileId, password },
    headers: {
      Authorization: `Bearer ${session.access_token}`
    }
  });

  if (error) {
    throw error;
  }
};

const CreateProfile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const [photoPreview, setPhotoPreview] = useState<string>("");
  const [profileImagePreview, setProfileImagePreview] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { subscription, isFree, isPremium, isPro } = useSubscription();
  const [profileCount, setProfileCount] = useState<number>(0);
  const [profileLimit, setProfileLimit] = useState<number | null>(null);

  const [profile, setProfile] = useState<EmergencyProfile>({
    id: "",
    photo: "",
    profileImage: "",
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    bloodType: "",
    emergencyContacts: [{ name: "", phone: "", relation: "" }],
    medications: [{ name: "", notes: "" }],
    allergies: [{ name: "", notes: "" }],
    conditions: [{ name: "", notes: "", attackStatus: "" }],
    additionalNotes: "",
    passwordProtected: false,
    password: "",
  });

  useEffect(() => {
    const initProfile = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Bitte melden Sie sich an, um ein Profil zu erstellen");
        navigate("/auth");
        return;
      }

      const { count } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true })
        .eq("user_id", session.user.id);
      
      if (count !== null) {
        setProfileCount(count);
      }

      if (isFree) {
        setProfileLimit(1);
      } else if (isPremium) {
        setProfileLimit(2);
      } else if (isPro) {
        setProfileLimit(5);
      } else {
        setProfileLimit(1);
      }

      if (editId) {
        try {
          const { data, error } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", editId)
            .eq("user_id", session.user.id)
            .maybeSingle();

          if (error || !data) {
            toast.error("Profil nicht gefunden");
            navigate("/");
            return;
          }

          // Parse JSONB data
          const medications = Array.isArray(data.medications) 
            ? data.medications.map((m: any) => 
                typeof m === 'string' ? { name: m, notes: '' } : m
              )
            : [{ name: "", notes: "" }];
          
          const allergies = Array.isArray(data.allergies)
            ? data.allergies.map((a: any) => 
                typeof a === 'string' ? { name: a, notes: '' } : a
              )
            : [{ name: "", notes: "" }];
          
          const conditions = Array.isArray(data.conditions)
            ? data.conditions.map((c: any) => 
                typeof c === 'string' ? { name: c, notes: '', attackStatus: '' } : c
              )
            : [{ name: "", notes: "", attackStatus: "" }];

          setProfile({
            id: data.id,
            photo: data.photo_url,
            profileImage: data.profile_image_url || "",
            firstName: data.first_name,
            lastName: data.last_name,
            dateOfBirth: data.date_of_birth || "",
            bloodType: data.blood_type || "",
            emergencyContacts: Array.isArray(data.emergency_contacts) ? data.emergency_contacts as unknown as EmergencyContact[] : [{ name: "", phone: "", relation: "" }],
            medications: medications.length > 0 ? medications : [{ name: "", notes: "" }],
            allergies: allergies.length > 0 ? allergies : [{ name: "", notes: "" }],
            conditions: conditions.length > 0 ? conditions : [{ name: "", notes: "", attackStatus: "" }],
            additionalNotes: data.additional_notes || "",
            passwordProtected: data.password_protected || false,
            password: "",
          });
          setPhotoPreview(data.photo_url);
          if (data.profile_image_url) {
            setProfileImagePreview(data.profile_image_url);
          }
          setIsEditMode(true);

          // Check lock status via confirmation timestamp + plan
          try {
            const { data: conf } = await supabase
              .from("profile_confirmations")
              .select("last_confirmed_at")
              .eq("profile_id", data.id)
              .maybeSingle();
            if (conf?.last_confirmed_at) {
              const interval = isPro ? Infinity : isPremium ? 180 : 30;
              const days = Math.floor((Date.now() - new Date(conf.last_confirmed_at).getTime()) / 86400000);
              if (interval !== Infinity && days > interval) setIsLocked(true);
            }
          } catch (e) {
            console.warn("lock check failed", e);
          }
        } catch (error) {
          console.error("Error:", error);
          toast.error("Ein Fehler ist aufgetreten");
          navigate("/");
          return;
        }
      }

      setLoading(false);
    };
    
    initProfile();
  }, [navigate, editId, isFree, isPremium, isPro]);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("Bild zu groß. Maximale Größe: 5MB");
      e.target.value = "";
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Ungültiger Dateityp. Nur JPEG, PNG und WebP sind erlaubt.");
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setPhotoPreview(result);
      setProfile({ ...profile, photo: result });
    };
    reader.readAsDataURL(file);
  };

  const handleProfileImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isPremium && !isPro) {
      toast.error("Individualisierbare QR-Codes sind nur für Premium- und Pro-Nutzer verfügbar");
      return;
    }

    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("Bild zu groß. Maximale Größe: 2MB");
      e.target.value = "";
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Ungültiger Dateityp. Nur JPEG, PNG und WebP sind erlaubt.");
      e.target.value = "";
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const fileExt = file.name.split('.').pop();
      const fileName = `${session.user.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-images')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('profile-images')
        .getPublicUrl(fileName);

      setProfileImagePreview(publicUrl);
      setProfile({ ...profile, profileImage: publicUrl });
      toast.success("Profilbild erfolgreich hochgeladen");
    } catch (error) {
      console.error("Error uploading profile image:", error);
      toast.error("Fehler beim Hochladen des Profilbildes");
    }
  };

  const addEmergencyContact = () => {
    if (isFree && profile.emergencyContacts.filter(c => c.name || c.phone).length >= 2) {
      toast.error("Kostenlose Konten können maximal 2 Notfallkontakte hinzufügen");
      return;
    }
    setProfile({
      ...profile,
      emergencyContacts: [...profile.emergencyContacts, { name: "", phone: "", relation: "" }],
    });
  };

  const removeEmergencyContact = (index: number) => {
    const contacts = profile.emergencyContacts.filter((_, i) => i !== index);
    setProfile({ ...profile, emergencyContacts: contacts });
  };

  const updateEmergencyContact = (index: number, field: string, value: string) => {
    const contacts = [...profile.emergencyContacts];
    contacts[index] = { ...contacts[index], [field]: value };
    setProfile({ ...profile, emergencyContacts: contacts });
  };

  const addMedication = () => {
    if (isFree && profile.medications.filter(m => m.name.trim()).length >= 2) {
      toast.error("Kostenlose Konten können maximal 2 Medikamente hinzufügen");
      return;
    }
    setProfile({
      ...profile,
      medications: [...profile.medications, { name: "", notes: "" }],
    });
  };

  const removeMedication = (index: number) => {
    setProfile({
      ...profile,
      medications: profile.medications.filter((_, i) => i !== index),
    });
  };

  const updateMedication = (index: number, field: 'name' | 'notes', value: string) => {
    const medications = [...profile.medications];
    medications[index] = { ...medications[index], [field]: value };
    setProfile({ ...profile, medications });
  };

  const addAllergy = () => {
    if (isFree && profile.allergies.filter(a => a.name.trim()).length >= 2) {
      toast.error("Kostenlose Konten können maximal 2 Allergien hinzufügen");
      return;
    }
    setProfile({
      ...profile,
      allergies: [...profile.allergies, { name: "", notes: "" }],
    });
  };

  const removeAllergy = (index: number) => {
    setProfile({
      ...profile,
      allergies: profile.allergies.filter((_, i) => i !== index),
    });
  };

  const updateAllergy = (index: number, field: 'name' | 'notes', value: string) => {
    const allergies = [...profile.allergies];
    allergies[index] = { ...allergies[index], [field]: value };
    setProfile({ ...profile, allergies });
  };

  const addCondition = () => {
    if (isFree && profile.conditions.filter(c => c.name.trim()).length >= 2) {
      toast.error("Kostenlose Konten können maximal 2 Vorerkrankungen hinzufügen");
      return;
    }
    setProfile({
      ...profile,
      conditions: [...profile.conditions, { name: "", notes: "", attackStatus: "" }],
    });
  };

  const removeCondition = (index: number) => {
    setProfile({
      ...profile,
      conditions: profile.conditions.filter((_, i) => i !== index),
    });
  };

  const updateCondition = (index: number, field: 'name' | 'notes' | 'attackStatus', value: string) => {
    const conditions = [...profile.conditions];
    conditions[index] = { ...conditions[index], [field]: value };
    setProfile({ ...profile, conditions });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLocked) {
      toast.error("Dieses Profil ist gesperrt. Bitte zuerst per Bestätigungs-Link reaktivieren.");
      return;
    }

    if (!profile.firstName.trim() || !profile.lastName.trim()) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    // Free user limits validation
    if (isFree) {
      const filledAllergies = profile.allergies.filter(a => a.name.trim() !== "");
      const filledConditions = profile.conditions.filter(c => c.name.trim() !== "");
      const filledMedications = profile.medications.filter(m => m.name.trim() !== "");
      const filledContacts = profile.emergencyContacts.filter(c => c.name.trim() !== "");

      if (filledAllergies.length > 2) {
        toast.error("Kostenlose Konten können maximal 2 Allergien speichern. Upgraden Sie für unbegrenzte Einträge.");
        return;
      }
      if (filledConditions.length > 2) {
        toast.error("Kostenlose Konten können maximal 2 Vorerkrankungen speichern. Upgraden Sie für unbegrenzte Einträge.");
        return;
      }
      if (filledMedications.length > 2) {
        toast.error("Kostenlose Konten können maximal 2 Medikamente speichern. Upgraden Sie für unbegrenzte Einträge.");
        return;
      }
      if (filledContacts.length > 2) {
        toast.error("Kostenlose Konten können maximal 2 Notfallkontakte speichern. Upgraden Sie für unbegrenzte Einträge.");
        return;
      }

      // Check if premium features were used
      const hasPremiumFeatures = 
        profile.medications.some(m => m.notes && m.notes.trim()) ||
        profile.allergies.some(a => a.notes && a.notes.trim()) ||
        profile.conditions.some(c => (c.notes && c.notes.trim()) || (c.attackStatus && c.attackStatus));

      if (hasPremiumFeatures) {
        toast.error("Erweiterte Detailfelder (Notizen & Schubstatus) sind nur für Premium-Nutzer verfügbar.");
        return;
      }
    }

    if (!isEditMode && profileLimit !== null && profileCount >= profileLimit) {
      setShowUpgradeModal(true);
      return;
    }

    if (profile.passwordProtected && !profile.password && !isEditMode) {
      toast.error("Bitte geben Sie ein Passwort ein");
      return;
    }

    if (profile.passwordProtected && profile.password) {
      const passwordValidation = profilePasswordSchema.safeParse(profile.password);
      if (!passwordValidation.success) {
        passwordValidation.error.errors.forEach(err => {
          toast.error(err.message);
        });
        return;
      }
    }

    try {
      // Filter out empty entries
      const filteredProfile = {
        ...profile,
        emergencyContacts: profile.emergencyContacts.filter(c => c.name || c.phone),
        medications: profile.medications.filter(m => m.name.trim()),
        allergies: profile.allergies.filter(a => a.name.trim()),
        conditions: profile.conditions.filter(c => c.name.trim()),
      };

      const validationResult = profileSchema.safeParse({
        firstName: filteredProfile.firstName,
        lastName: filteredProfile.lastName,
        dateOfBirth: filteredProfile.dateOfBirth,
        bloodType: filteredProfile.bloodType,
        medications: filteredProfile.medications,
        allergies: filteredProfile.allergies,
        conditions: filteredProfile.conditions,
        additionalNotes: filteredProfile.additionalNotes,
        emergencyContacts: filteredProfile.emergencyContacts,
      });

      if (!validationResult.success) {
        validationResult.error.errors.forEach(err => {
          toast.error(err.message);
        });
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Sitzung abgelaufen. Bitte melden Sie sich erneut an");
        return;
      }

      const profileData = {
        user_id: session.user.id,
        photo_url: profile.photo,
        profile_image_url: profile.profileImage || null,
        first_name: profile.firstName,
        last_name: profile.lastName,
        date_of_birth: profile.dateOfBirth || null,
        blood_type: profile.bloodType || null,
        emergency_contacts: filteredProfile.emergencyContacts as any,
        medications: filteredProfile.medications as any,
        allergies: filteredProfile.allergies as any,
        conditions: filteredProfile.conditions as any,
        additional_notes: profile.additionalNotes || null,
        password_protected: profile.passwordProtected,
      };

      if (isEditMode && profile.id) {
        const { error } = await supabase
          .from("profiles")
          .update(profileData)
          .eq("id", profile.id)
          .eq("user_id", session.user.id);

        if (error) {
          console.error("Error updating profile:", error);
          toast.error("Fehler beim Aktualisieren des Profils");
          return;
        }

        if (profile.password && profile.passwordProtected) {
          await setProfilePassword(profile.id, profile.password);
        }

        toast.success("Profil erfolgreich aktualisiert!");
        navigate(`/qr/${profile.id}`);
      } else {
        const { data, error } = await supabase
          .from("profiles")
          .insert(profileData)
          .select()
          .single();

        if (error) {
          console.error("Error creating profile:", error);
          toast.error("Fehler beim Erstellen des Profils");
          return;
        }

        if (profile.password && profile.passwordProtected && data) {
          await setProfilePassword(data.id, profile.password);
        }

        toast.success("Profil erfolgreich erstellt!");
        trackProfileCreation(data.id);
        
        try {
          await supabase.functions.invoke("send-confirmation-email", {
            body: { profileId: data.id },
          });
        } catch (emailError) {
          console.error("Error sending confirmation email:", emailError);
        }
        
        navigate(`/qr/${data.id}`);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Ein Fehler ist aufgetreten");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-subtle py-12 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Wird geladen...</p>
        </div>
      </div>
    );
  }

  const filledMedications = profile.medications.filter(m => m.name.trim());
  const filledAllergies = profile.allergies.filter(a => a.name.trim());
  const filledConditions = profile.conditions.filter(c => c.name.trim());
  const filledContacts = profile.emergencyContacts.filter(c => c.name.trim());

  return (
    <div className="min-h-screen bg-gradient-subtle py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Zurück
          </Button>
        </Link>

        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">
            Notfallprofil <span className="gradient-text">{isEditMode ? "bearbeiten" : "erstellen"}</span>
          </h1>
          <p className="text-muted-foreground">
            {isEditMode 
              ? "Aktualisieren Sie Ihre medizinischen Notfallinformationen." 
              : "Füllen Sie die folgenden Informationen aus, um Ihren persönlichen Notfall-QR-Code zu generieren."
            }
          </p>
        </div>

        {isLocked && (
          <Card className="p-4 mb-6 border-destructive/40 bg-destructive/10">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 text-destructive">🔒</div>
              <div className="space-y-1">
                <p className="font-semibold text-destructive">Profil gesperrt (nur lesend)</p>
                <p className="text-sm text-destructive/90">
                  Die 30-Tage-Frist zur Bestätigung ist abgelaufen. Änderungen sind erst wieder möglich, sobald du das Profil per Bestätigungs-Mail reaktivierst. Ohne Bestätigung wird das Profil nach insgesamt 90 Tagen automatisch gelöscht.
                </p>
              </div>
            </div>
          </Card>
        )}
        <fieldset disabled={isLocked} className={isLocked ? "opacity-70 pointer-events-none" : ""}>
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Photo Upload */}
          <Card className="p-6 glass">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary" />
              Foto hochladen (optional)
            </h2>
            <div className="flex flex-col items-center gap-4">
              <div className="w-40 h-40 rounded-full border-4 border-primary/20 overflow-hidden bg-muted flex items-center justify-center">
                {photoPreview ? (
                  <img src={photoPreview} alt="Vorschau" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-20 h-20 text-muted-foreground" />
                )}
              </div>
              <Input
                id="photo"
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="max-w-xs"
              />
            </div>
          </Card>

          {/* Premium: Profile Image Upload */}
          {(isPremium || isPro) && (
            <Card className="p-6 glass border-primary/30">
              <div className="flex items-center gap-2 mb-4">
                <Image className="h-5 w-5 text-primary" />
                <h2 className="text-2xl font-semibold">QR-Code Individualisierung</h2>
                <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">Premium</span>
              </div>
              <div className="flex flex-col items-center gap-4">
                <div className="w-40 h-40 rounded-lg border-4 border-primary/20 overflow-hidden bg-muted flex items-center justify-center">
                  {profileImagePreview ? (
                    <img src={profileImagePreview} alt="Profilbild Vorschau" className="w-full h-full object-cover" />
                  ) : (
                    <Image className="w-20 h-20 text-muted-foreground" />
                  )}
                </div>
                <Input
                  id="profileImage"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleProfileImageUpload}
                  className="max-w-xs"
                />
              </div>
            </Card>
          )}

          {/* Premium: Password Protection */}
          {(isPremium || isPro) && (
            <Card className="p-6 glass border-primary/30">
              <div className="flex items-center gap-2 mb-4">
                <Lock className="h-5 w-5 text-primary" />
                <h2 className="text-2xl font-semibold">Passwortschutz</h2>
                <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">Premium</span>
              </div>
              <div className="flex items-center gap-4">
                <Checkbox
                  id="passwordProtected"
                  checked={profile.passwordProtected}
                  onCheckedChange={(checked) => setProfile({ ...profile, passwordProtected: !!checked })}
                />
                <Label htmlFor="passwordProtected" className="mb-0">
                  Profil mit Passwort schützen
                </Label>
              </div>
              {profile.passwordProtected && (
                <div className="mt-4">
                  <Label htmlFor="password">Passwort *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={profile.password}
                    onChange={(e) => setProfile({ ...profile, password: e.target.value })}
                    required={profile.passwordProtected}
                    autoComplete="new-password"
                  />
                  <PasswordStrengthIndicator password={profile.password} />
                  <p className="text-sm text-muted-foreground mt-1">
                    Das Passwort muss mindestens 10 Zeichen lang sein und Großbuchstaben, Kleinbuchstaben, Zahlen und Sonderzeichen enthalten.
                  </p>
                </div>
              )}
            </Card>
          )}

          {/* Personal Information */}
          <Card className="p-6 glass">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              Persönliche Daten
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">Vorname *</Label>
                <Input
                  id="firstName"
                  value={profile.firstName}
                  onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="lastName">Nachname *</Label>
                <Input
                  id="lastName"
                  value={profile.lastName}
                  onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="dateOfBirth">Geburtsdatum</Label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={profile.dateOfBirth}
                  onChange={(e) => setProfile({ ...profile, dateOfBirth: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="bloodType">Blutgruppe</Label>
                <Input
                  id="bloodType"
                  value={profile.bloodType}
                  onChange={(e) => setProfile({ ...profile, bloodType: e.target.value })}
                  placeholder="z.B. A+"
                />
              </div>
            </div>
          </Card>

          {/* Emergency Contacts */}
          <Card className="p-6 glass">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold flex items-center gap-2">
                <Phone className="h-5 w-5 text-primary" />
                Notfallkontakte
              </h2>
              <Button 
                type="button" 
                onClick={addEmergencyContact} 
                size="sm" 
                variant="outline"
                disabled={isFree && filledContacts.length >= 2}
              >
                <Plus className="h-4 w-4 mr-2" />
                Hinzufügen
              </Button>
            </div>
            {isFree && filledContacts.length >= 2 && (
              <div className="mb-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
                <p className="text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-primary" />
                  Kostenlos: Bis zu 2 Kontakte.{" "}
                  <Button variant="link" className="h-auto p-0 text-primary" onClick={() => navigate("/pricing")}>
                    Upgraden für unbegrenzt
                  </Button>
                </p>
              </div>
            )}
            <div className="space-y-4">
              {profile.emergencyContacts.map((contact, index) => (
                <div key={index} className="p-4 bg-background/50 rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-muted-foreground">Kontakt {index + 1}</span>
                    {profile.emergencyContacts.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeEmergencyContact(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid md:grid-cols-3 gap-3">
                    <Input
                      placeholder="Name"
                      value={contact.name}
                      onChange={(e) => updateEmergencyContact(index, "name", e.target.value)}
                    />
                    <Input
                      placeholder="Telefonnummer"
                      value={contact.phone}
                      onChange={(e) => updateEmergencyContact(index, "phone", e.target.value)}
                    />
                    <Input
                      placeholder="Beziehung"
                      value={contact.relation}
                      onChange={(e) => updateEmergencyContact(index, "relation", e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Medications */}
          <Card className="p-6 glass">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold flex items-center gap-2">
                <Pill className="h-5 w-5 text-primary" />
                Medikamente
              </h2>
              <Button 
                type="button" 
                onClick={addMedication} 
                size="sm" 
                variant="outline"
                disabled={isFree && filledMedications.length >= 2}
              >
                <Plus className="h-4 w-4 mr-2" />
                Hinzufügen
              </Button>
            </div>
            {isFree && filledMedications.length >= 2 && (
              <div className="mb-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
                <p className="text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-primary" />
                  Kostenlos: Bis zu 2 Medikamente.{" "}
                  <Button variant="link" className="h-auto p-0 text-primary" onClick={() => navigate("/pricing")}>
                    Upgraden für unbegrenzt + Notizen
                  </Button>
                </p>
              </div>
            )}
            <div className="space-y-3">
              {profile.medications.map((med, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Medikament eingeben"
                      value={med.name}
                      onChange={(e) => updateMedication(index, 'name', e.target.value)}
                    />
                    {profile.medications.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMedication(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {(isPremium || isPro) && med.name.trim() && (
                    <Collapsible>
                      <CollapsibleTrigger className="flex items-center gap-2 text-sm text-primary hover:underline">
                        <Plus className="h-3 w-3" />
                        Notizen hinzufügen
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-2">
                        <Textarea
                          placeholder="Zusätzliche Informationen zum Medikament..."
                          value={med.notes || ""}
                          onChange={(e) => updateMedication(index, 'notes', e.target.value)}
                          className="min-h-[60px]"
                        />
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Allergies */}
          <Card className="p-6 glass">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-primary" />
                Allergien
              </h2>
              <Button 
                type="button" 
                onClick={addAllergy} 
                size="sm" 
                variant="outline"
                disabled={isFree && filledAllergies.length >= 2}
              >
                <Plus className="h-4 w-4 mr-2" />
                Hinzufügen
              </Button>
            </div>
            {isFree && filledAllergies.length >= 2 && (
              <div className="mb-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
                <p className="text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-primary" />
                  Kostenlos: Bis zu 2 Allergien.{" "}
                  <Button variant="link" className="h-auto p-0 text-primary" onClick={() => navigate("/pricing")}>
                    Upgraden für unbegrenzt + Notizen
                  </Button>
                </p>
              </div>
            )}
            <div className="space-y-3">
              {profile.allergies.map((allergy, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Allergie eingeben"
                      value={allergy.name}
                      onChange={(e) => updateAllergy(index, 'name', e.target.value)}
                    />
                    {profile.allergies.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAllergy(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {(isPremium || isPro) && allergy.name.trim() && (
                    <Collapsible>
                      <CollapsibleTrigger className="flex items-center gap-2 text-sm text-primary hover:underline">
                        <Plus className="h-3 w-3" />
                        Notizen hinzufügen
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-2">
                        <Textarea
                          placeholder="Zusätzliche Informationen zur Allergie..."
                          value={allergy.notes || ""}
                          onChange={(e) => updateAllergy(index, 'notes', e.target.value)}
                          className="min-h-[60px]"
                        />
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Conditions */}
          <Card className="p-6 glass">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Vorerkrankungen
              </h2>
              <Button 
                type="button" 
                onClick={addCondition} 
                size="sm" 
                variant="outline"
                disabled={isFree && filledConditions.length >= 2}
              >
                <Plus className="h-4 w-4 mr-2" />
                Hinzufügen
              </Button>
            </div>
            {isFree && filledConditions.length >= 2 && (
              <div className="mb-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
                <p className="text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-primary" />
                  Kostenlos: Bis zu 2 Vorerkrankungen.{" "}
                  <Button variant="link" className="h-auto p-0 text-primary" onClick={() => navigate("/pricing")}>
                    Upgraden für unbegrenzt + Schubstatus + Notizen
                  </Button>
                </p>
              </div>
            )}
            <div className="space-y-3">
              {profile.conditions.map((condition, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Vorerkrankung eingeben"
                      value={condition.name}
                      onChange={(e) => updateCondition(index, 'name', e.target.value)}
                    />
                    {profile.conditions.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCondition(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {(isPremium || isPro) && condition.name.trim() && (
                    <div className="space-y-2">
                      <Select
                        value={condition.attackStatus || undefined}
                        onValueChange={(value) => updateCondition(index, 'attackStatus', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Schubstatus wählen (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="stabil">Chronisch stabil</SelectItem>
                          <SelectItem value="remission">In Remission</SelectItem>
                          <SelectItem value="akut">Akuter Schub</SelectItem>
                        </SelectContent>
                      </Select>
                      <Collapsible>
                        <CollapsibleTrigger className="flex items-center gap-2 text-sm text-primary hover:underline">
                          <Plus className="h-3 w-3" />
                          Notizen hinzufügen
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-2">
                          <Textarea
                            placeholder="Details zur Erkrankung..."
                            value={condition.notes || ""}
                            onChange={(e) => updateCondition(index, 'notes', e.target.value)}
                            className="min-h-[60px]"
                          />
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Additional Notes */}
          <Card className="p-6 glass">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Zusätzliche Notizen
            </h2>
            <Textarea
              placeholder="Weitere wichtige Informationen für Notfallhelfer..."
              value={profile.additionalNotes}
              onChange={(e) => setProfile({ ...profile, additionalNotes: e.target.value })}
              className="min-h-[120px]"
            />
          </Card>

          {/* Submit Button */}
          <div className="flex gap-4 justify-end">
            <Button type="button" variant="outline" onClick={() => navigate("/")}>
              Abbrechen
            </Button>
            <Button type="submit" className="bg-gradient-primary" disabled={isLocked}>
              {isEditMode ? "Profil aktualisieren" : "Profil erstellen"}
            </Button>
          </div>
        </form>
        </fieldset>
      </div>

      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
      />
    </div>
  );
};

export default CreateProfile;
